import React from "react";
import { Button, Card, Collapse, Result, Space, Typography, message } from "antd";
import { CopyOutlined, ReloadOutlined, WarningOutlined } from "@ant-design/icons";
import { logger } from "utils/logger";

/**
 * Captura errores no manejados en el árbol de componentes y muestra
 * una UI de fallback moderna, con detalle técnico expandible para soporte.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    logger.error("ErrorBoundary capturó un error:", error, info);
    this.setState({ info });
  }

  handleReload = () => {
    this.setState({ hasError: false, error: null, info: null });
    window.location.reload();
  };

  handleCopy = async () => {
    const { error, info } = this.state;
    const text = [
      `Error: ${error?.message || error}`,
      "",
      "Stack:",
      error?.stack || "(sin stack)",
      "",
      "Component stack:",
      info?.componentStack || "(sin component stack)",
    ].join("\n");
    try {
      await navigator.clipboard.writeText(text);
      message.success("Detalle del error copiado al portapapeles");
    } catch {
      message.error("No se pudo copiar el detalle");
    }
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    const { error, info } = this.state;
    const detail = [
      `Error: ${error?.message || String(error)}`,
      "",
      "Stack:",
      error?.stack || "(sin stack)",
      "",
      "Component stack:",
      info?.componentStack || "(sin component stack)",
    ].join("\n");

    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 24,
          background: "linear-gradient(135deg, #f5f0fa 0%, #ffffff 100%)",
        }}
      >
        <Card
          style={{
            maxWidth: 640,
            width: "100%",
            borderRadius: 16,
            boxShadow: "0 12px 40px rgba(121, 30, 163, 0.12)",
            border: "1px solid #ede7f6",
          }}
          bodyStyle={{ padding: 32 }}
        >
          <Result
            status="error"
            icon={
              <WarningOutlined
                style={{
                  fontSize: 56,
                  color: "#791ea3",
                }}
              />
            }
            title={
              <Typography.Title level={3} style={{ margin: 0, color: "#2d1b4e" }}>
                Ocurrió un error inesperado
              </Typography.Title>
            }
            subTitle={
              <Typography.Text type="secondary" style={{ fontSize: 14 }}>
                La aplicación encontró un problema. Podés recargar la página o
                copiar el detalle técnico para enviarlo a soporte.
              </Typography.Text>
            }
            extra={
              <Space size="middle" wrap>
                <Button
                  type="primary"
                  icon={<ReloadOutlined />}
                  onClick={this.handleReload}
                  size="large"
                  style={{ borderRadius: 8 }}
                >
                  Recargar página
                </Button>
                <Button
                  icon={<CopyOutlined />}
                  onClick={this.handleCopy}
                  size="large"
                  style={{ borderRadius: 8 }}
                >
                  Copiar detalle
                </Button>
              </Space>
            }
          />

          <Collapse
            ghost
            style={{ marginTop: 8 }}
            items={[
              {
                key: "detail",
                label: (
                  <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                    Ver detalle técnico (para soporte)
                  </Typography.Text>
                ),
                children: (
                  <pre
                    style={{
                      margin: 0,
                      padding: 12,
                      background: "#f7f5fa",
                      borderRadius: 8,
                      fontSize: 12,
                      lineHeight: 1.5,
                      color: "#4a3b6b",
                      overflow: "auto",
                      maxHeight: 280,
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                    }}
                  >
                    {detail}
                  </pre>
                ),
              },
            ]}
          />
        </Card>
      </div>
    );
  }
}

export default ErrorBoundary;
